import { useInterval, useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { isUndefined } from 'lodash';
import React from 'react';

import { getGroupThroughputOverview, getWorkstationPerformanceLastHour } from '@/pages/workstation-dashboard/api';
import { LOOP_TIME, MAX_WORKSTATION_PER_PAGE } from '@/pages/workstation-dashboard/const';
import { getUrlParams } from '@/utils/url';

import { OriginGroupItem } from '../defined';
import { transformStrToArr } from '../utils/common';
import useRefreshInterval from './use-refresh-interval';

export interface TvModeParams {
  type: 'single' | 'group';
  workstation_group_id: string;
  workstation_group_name: string;
}

export const useTvMode = () => {
  const groupIndexRef = React.useRef<number>(0);
  const [lastFetchUpdateTime, setLastFetchUpdateTime] = React.useState<number>(0);
  const [curGroupIndex, setCurGroupIndex] = React.useState<number>(0);
  const [curPage, setCurPage] = React.useState<number>(0);
  const [groupList, setGroupList] = React.useState<OriginGroupItem[]>([]);
  const { type, workstation_group_name, workstation_group_id, activity_type_list, workstation_group_id_list } = getUrlParams(window.location.href);

  const curGroupItem = React.useMemo(() => {
    if (isUndefined(curGroupIndex)) return {};
    return groupList[curGroupIndex] || {};
  }, [groupList, curGroupIndex]);

  const {
    loading: groupLoading,
    data: groupData,
    run: manualGetGroupThroughputOverview
  } = useRequest(getGroupThroughputOverview, {
    manual: true,
    onSuccess: (data) => {
      const list = data?.data?.list || [];
      const curGroupListMap = groupList.reduce((acc, cur, index) => {
        if (cur.workstation_group) {
          acc[cur.workstation_group] = index + 1;
        }
        return acc;
      }, {} as Record<string, number>);
      const existGroupList = list
        .filter(item => item.workstation_group ? curGroupListMap[item.workstation_group] : false)
        .sort((a, b) => {
          const aIndex = a.workstation_group ? curGroupListMap[a.workstation_group] : 0;
          const bIndex = b.workstation_group ? curGroupListMap[b.workstation_group] : 0;
          return aIndex - bIndex;
        });
      const nonExistGroupList = list.filter(item => item.workstation_group ? !curGroupListMap[item.workstation_group] : false);
      const newGroupList = existGroupList.concat(nonExistGroupList);
      // check which group is current group
      const curWorkstationGroupId = curGroupItem.workstation_group_id ? curGroupItem.workstation_group_id : +workstation_group_id;
      const groupIndex = newGroupList.findIndex(item => item.workstation_group_id === curWorkstationGroupId) ?? -1;
      setGroupList(newGroupList);
      setCurGroupIndex(groupIndex);
      setLastFetchUpdateTime(dayjs().unix());
      groupIndexRef.current = groupIndex;
    }
  });

  const {
    loading: workstationListLoading,
    data: workstationListData,
    run: manualGetWorkstationPerformanceLastHour
  } = useRequest(getWorkstationPerformanceLastHour, { manual: true });

  const lastUpdateTime = React.useMemo(() => {
    return lastFetchUpdateTime ? dayjs(lastFetchUpdateTime * 1000).format('YYYY/MM/DD HH:mm:ss') : '';
  }, [lastFetchUpdateTime]);

  const curWorkstationList = React.useMemo(() => {
    const arr = workstationListData?.data?.items || [];
    return arr.slice(curPage * MAX_WORKSTATION_PER_PAGE, (curPage + 1) * MAX_WORKSTATION_PER_PAGE);
  }, [workstationListData?.data?.items, curPage]);

  const totalPages = React.useMemo(() => {
    const workstationList = workstationListData?.data?.items || [];
    if (!workstationList.length) return 0;
    return Math.ceil(workstationList.length / MAX_WORKSTATION_PER_PAGE);
  }, [workstationListData?.data?.items]);

  const totalGroups = React.useMemo(() => {
    if (type === 'single') return 1;
    return groupList.length;
  }, [groupList.length, type]);

  useInterval(() => {
    if (!totalPages || groupIndexRef.current < 0) return;
    setCurPage(page => {
      if (page + 1 >= totalPages) {
        if (type === 'group') {
          setCurGroupIndex(groupIndex => {
            if (groupIndex + 1 >= totalGroups) {
              return 0;
            }
            return groupIndex + 1;
          });
        }
        return 0;
      }
      return page + 1;
    });
  }, LOOP_TIME);

  const overviewParams = React.useMemo(() => {
    return type === 'group'
      ? {
          activity_type_list: transformStrToArr(activity_type_list),
          workstation_group_id_list: transformStrToArr(workstation_group_id_list)
        }
      : {};
  }, [activity_type_list, type, workstation_group_id_list]);

  const refresh = React.useCallback(() => {
    if (!overviewParams) return;
    manualGetGroupThroughputOverview(overviewParams);
  }, [manualGetGroupThroughputOverview, overviewParams]);

  React.useEffect(() => {
    const workstation_group_id = curGroupItem?.workstation_group_id;
    if (!workstation_group_id) return;
    manualGetWorkstationPerformanceLastHour({ workstation_group_id });
  }, [curGroupItem?.workstation_group_id, manualGetWorkstationPerformanceLastHour]);

  useRefreshInterval(refresh, true);

  return {
    type,
    curGroupIndex,
    curPage,
    workstation_group_name,
    workstation_group_id,
    groupLoading,
    workstationListLoading,
    groupData,
    groupList,
    workstationListData,
    curGroupItem,
    totalGroups,
    lastUpdateTime,
    curWorkstationList,
    totalPages
  };
};
